import {
  Disclaimer,
  Div,
  formatAmount,
  H3,
  H5,
  lightTheme,
  ListSpinner,
  ModalPortal,
  NumberType,
  P,
  Separator,
} from '@vgrubs/components';
import { saasRange } from '../SaasDetails/saasDetails.helpers';
import { financeProviderLabel } from '../serviceLegend';
import { ServiceFeeTable } from './ServiceFeeTable';
import { useServiceFeeDetails } from './useServiceFeeDetails';

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  /** The overview's range (yyyy-MM-dd), so the total matches its row. */
  from: string;
  to: string;
  periodLabel?: string;
};

const usd = (cents: number) => formatAmount(cents, NumberType.USD);

const openLocation = (locationId: number) =>
  window.open(`/locations/${locationId}`, '_blank', 'noopener,noreferrer');

const SummaryNumber = ({ label, cents }: { label: string; cents: number }) => (
  <div>
    <P $m="0" $color={lightTheme.lightGray}>
      {label}
    </P>
    <H3 $m="0" $color={lightTheme.green}>
      {usd(cents)}
    </H3>
  </div>
);

/**
 * Where "Service Fee" on the Finances overview came from: the fee partner API
 * orders (Kanekt365) charge the customer, split into card (charged through
 * our Stripe) and cash (taken from the location's balance), by partner and by
 * location. Opened from that row of the Gross and Net Revenue cards, for the
 * same range.
 */
export const ServiceFeeDetailsModal = ({
  isOpen,
  closeModal,
  from,
  to,
  periodLabel,
}: Props) => {
  const range = saasRange(from, to);
  const { details, isLoading, isError } = useServiceFeeDetails(range, isOpen);

  // Location rows name their partners the way the partner table does: by the
  // name the backend sends for each provider.
  const partnerName = (provider: string) =>
    details?.byProvider.find((row) => row.provider === provider)
      ?.providerName || financeProviderLabel(provider);

  return (
    <ModalPortal
      isOpen={isOpen}
      closeModal={closeModal}
      backgroundColor={lightTheme.white}
      padding="24px"
      logo={false}
      width="1000px"
      maxHeight="80vh"
      modalOverflow="auto"
      titleWithExit="Service Fee - where it came from"
      subtitle={periodLabel}
      // Above the cards' "View all" modal, as the Monthly Saas details are.
      zIndex={135}
    >
      {isLoading && (
        <ListSpinner loading height="160px" heightSM="160px" mb="0" mbSM="0" />
      )}

      {isError && (
        <P $m="24px 0" $center $color={lightTheme.primary}>
          The service fee details could not be loaded.
        </P>
      )}

      {details && (
        <>
          <Div $flex $wrap $gap="40px" $gapSM="24px" $mb="4">
            <SummaryNumber label="Total" cents={details.total} />
            <SummaryNumber label="Card" cents={details.card} />
            <SummaryNumber label="Cash" cents={details.cash} />
          </Div>
          <Disclaimer $m="0 0 16px" $color={lightTheme.lightGray}>
            Card is charged through our Stripe; cash is taken from the
            location&apos;s balance. Completed partner API orders only, by order
            date.
          </Disclaimer>

          <Separator $m="0 0 16px" />
          <H5 $mb="12">By partner</H5>
          <ServiceFeeTable
            nameLabel="Partner"
            emptyText="No service fee in this period."
            rows={details.byProvider.map((row) => ({
              key: row.provider,
              name: partnerName(row.provider),
              card: row.card,
              cash: row.cash,
              total: row.total,
              orders: row.orders,
            }))}
          />

          <H5 $m="24px 0 12px">By location</H5>
          <ServiceFeeTable
            nameLabel="Location"
            emptyText="No service fee in this period."
            rows={details.byLocation.map((row) => ({
              key: row.locationId,
              name: row.locationName,
              hint: row.providers.map(partnerName).join(', '),
              onOpen: () => openLocation(row.locationId),
              card: row.card,
              cash: row.cash,
              total: row.total,
              orders: row.orders,
            }))}
          />
        </>
      )}
    </ModalPortal>
  );
};
